import { hydrateSceneryAirport } from "./airports";
import { sanitizeClockIso } from "./clock";
import { isCharterInboxItem } from "./voiceDesk";
import type { VoiceDeskSnapshot } from "./voiceDesk";
import type {
  CharterRequest,
  InboxKind,
  InquirySource,
  SceneryAirport,
  VoiceSnippet,
} from "./types";

function newId(prefix: string, now: Date): string {
  return `${prefix}-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

/** PSTN / phone legs stay off the desk — no Inbox dump, no sticky transcript. */
export function voiceMirrorsToDesk(phoneLeg: boolean): boolean {
  return !phoneLeg;
}

/**
 * Phone/PSTN and Duty Standby never persist CREW/DISPATCH turns on an Inbox row.
 * Desk-mic Call Dispatch (`voice_brief`) may still optionally mirror.
 */
export function inboxAllowsVoiceTranscript(kind?: InboxKind, phoneLeg = false): boolean {
  if (phoneLeg) return false;
  if (kind === "standby_call") return false;
  return true;
}

export function looksLikeVoiceTranscriptBody(body: string): boolean {
  return /\bCREW\b/.test(body) && /\bDISPATCH\b/.test(body);
}

export function jobCardEmailBody(input: {
  companyName: string;
  originIcao: string;
  destIcao: string;
  clientName?: string;
  kind: InboxKind;
}): string {
  const label = input.kind === "standby_call" ? "Duty Standby job card" : "Dispatch note";
  return [
    `From: ${input.companyName} Dispatch`,
    "",
    `${label} ${input.originIcao}–${input.destIcao}.`,
    input.clientName?.trim() ? `Client: ${input.clientName.trim()}` : "",
  ]
    .filter(Boolean)
    .join("\n");
}

export function hydrateInboxKind(row: CharterRequest): CharterRequest {
  const kind = row.kind === "voice_brief" || row.kind === "standby_call" ? row.kind : "charter";
  const source: InquirySource =
    row.source === "voice" ||
    row.source === "standby" ||
    row.source === "grok" ||
    row.source === "demo" ||
    row.source === "automatic" ||
    row.source === "trickle" ||
    row.source === "manual"
      ? row.source
      : kind === "standby_call"
        ? "standby"
        : kind === "voice_brief"
          ? "voice"
          : "demo";
  const allow = inboxAllowsVoiceTranscript(kind);
  const next: CharterRequest = {
    ...row,
    kind,
    source,
    thread: allow && Array.isArray(row.thread) ? row.thread : undefined,
  };
  if (!allow && looksLikeVoiceTranscriptBody(row.emailBody ?? "")) {
    next.emailBody = jobCardEmailBody({
      companyName: row.clientCompany || "Dispatch",
      originIcao: row.originIcao,
      destIcao: row.destIcao,
      clientName: row.clientName,
      kind,
    });
  }
  return next;
}

export function hydrateInboxList(list: CharterRequest[] | undefined): CharterRequest[] {
  if (!Array.isArray(list)) return [];
  return list.map((row) => {
    const next = hydrateInboxKind(row);
    const departureIso = sanitizeClockIso(next.departureIso);
    return departureIso === next.departureIso ? next : { ...next, departureIso };
  });
}

export type VoiceInboxDraft = {
  userText: string;
  reply: string;
  kind?: InboxKind;
  originIcao?: string;
  destIcao?: string;
  clientName?: string;
  aircraftId?: string;
  now?: Date;
};

function namesFor(icao: string, scenery: SceneryAirport[], fallback: string): { icao: string; name: string } {
  const hit = hydrateSceneryAirport(icao, scenery);
  if (hit) return { icao: hit.icao, name: hit.name };
  return { icao: icao.toUpperCase() || fallback, name: icao.toUpperCase() || fallback };
}

/** Persist a desk-mic voice turn, or a Standby job card without the phone conversation. */
export function inboxItemFromVoice(input: {
  draft: VoiceInboxDraft;
  snapshot: VoiceDeskSnapshot;
  scenery: SceneryAirport[];
  /** PSTN / phone legs never dump transcript even if kind is voice_brief. */
  phoneLeg?: boolean;
}): CharterRequest {
  const now = input.draft.now ?? new Date();
  const kind: InboxKind = input.draft.kind ?? "voice_brief";
  const source: InquirySource = kind === "standby_call" ? "standby" : "voice";
  const origin = namesFor(input.draft.originIcao || input.snapshot.homeIcao, input.scenery, input.snapshot.homeIcao);
  const destRaw = input.draft.destIcao || input.snapshot.homeIcao;
  const dest = namesFor(destRaw, input.scenery, input.snapshot.homeIcao);
  const includeTranscript = inboxAllowsVoiceTranscript(kind, input.phoneLeg);
  const kicker = kind === "standby_call" ? "STANDBY" : "DISPATCH";
  const subject = `${kicker} — ${origin.icao}–${dest.icao}`;
  const clientName = input.draft.clientName?.trim() || "Dispatch";
  const body = includeTranscript
    ? [
        `From: ${input.snapshot.companyName} Dispatch`,
        "",
        `CREW`,
        input.draft.userText,
        "",
        `DISPATCH`,
        input.draft.reply,
      ].join("\n")
    : jobCardEmailBody({
        companyName: input.snapshot.companyName,
        originIcao: origin.icao,
        destIcao: dest.icao,
        clientName,
        kind,
      });
  const thread: VoiceSnippet[] | undefined = includeTranscript
    ? [
        { role: "crew", text: input.draft.userText, atUtc: now.toISOString() },
        { role: "dispatch", text: input.draft.reply, atUtc: now.toISOString() },
      ]
    : undefined;

  return {
    id: newId(kind === "standby_call" ? "stb" : "voice", now),
    createdAt: now.toISOString(),
    source,
    kind,
    status: "new",
    clientName,
    clientEmail: "dispatch@ops.local",
    clientCompany: input.snapshot.companyName,
    originIcao: origin.icao,
    destIcao: dest.icao,
    originName: origin.name,
    destName: dest.name,
    departureIso: now.toISOString(),
    paxCount: 0,
    cargoLb: 0,
    reason: kind === "standby_call" ? "Duty Standby call" : "Voice / text dispatch brief",
    specialRequests: "",
    emailSubject: subject,
    emailBody: body,
    suggestedAircraftId: input.draft.aircraftId ?? "",
    flightType: "corporate",
    thread,
  };
}

export function appendVoiceThread(row: CharterRequest, snippets: VoiceSnippet[], phoneLeg = false): CharterRequest {
  const hydrated = hydrateInboxKind(row);
  if (!inboxAllowsVoiceTranscript(hydrated.kind, phoneLeg) || !snippets.length) {
    return hydrated;
  }
  const thread = [...(hydrated.thread ?? []), ...snippets];
  const extra = snippets.map((s) => `${s.role === "crew" ? "CREW" : "DISPATCH"}\n${s.text}`).join("\n\n");
  return {
    ...hydrated,
    thread,
    emailBody: extra ? `${hydrated.emailBody}\n\n${extra}` : hydrated.emailBody,
  };
}

export function charterInboxIds(inbox: CharterRequest[]): string[] {
  return inbox.filter(isCharterInboxItem).map((r) => r.id);
}
